/**
 * Storage service
 * Abstraction layer for file storage (Supabase Storage / AWS S3)
 */

import { supabase } from "./supabase";
import type { StorageProvider } from "../types/document.types";

export interface StorageOptions {
  provider: StorageProvider;
  bucket: string;
  path: string;
  file: File | Blob;
  contentType?: string;
  cacheControl?: string;
  upsert?: boolean;
}

export interface StorageDownloadOptions {
  provider: StorageProvider;
  bucket: string;
  path: string;
}

export interface SignedUrlOptions {
  provider: StorageProvider;
  bucket: string;
  path: string;
  expiresIn: number; // seconds
}

/**
 * Upload file to storage
 */
export const uploadFile = async (options: StorageOptions): Promise<string> => {
  const { provider, bucket, path, file, contentType, cacheControl, upsert } =
    options;

  switch (provider) {
    case "supabase":
      return uploadToSupabase(bucket, path, file, {
        contentType,
        cacheControl,
        upsert,
      });

    case "s3":
      throw new Error("AWS S3 provider not implemented yet");

    case "google_cloud":
      throw new Error("Google Cloud Storage provider not implemented yet");

    case "azure":
      throw new Error("Azure Storage provider not implemented yet");

    default:
      throw new Error(`Unsupported storage provider: ${provider}`);
  }
};

/**
 * Download file from storage
 */
export const downloadFile = async (
  options: StorageDownloadOptions,
): Promise<Blob> => {
  const { provider, bucket, path } = options;

  switch (provider) {
    case "supabase":
      return downloadFromSupabase(bucket, path);

    case "s3":
      throw new Error("AWS S3 provider not implemented yet");

    case "google_cloud":
      throw new Error("Google Cloud Storage provider not implemented yet");

    case "azure":
      throw new Error("Azure Storage provider not implemented yet");

    default:
      throw new Error(`Unsupported storage provider: ${provider}`);
  }
};

/**
 * Get signed URL for temporary access
 */
export const getSignedUrl = async (
  options: SignedUrlOptions,
): Promise<string> => {
  const { provider, bucket, path, expiresIn } = options;

  switch (provider) {
    case "supabase":
      return getSupabaseSignedUrl(bucket, path, expiresIn);

    case "s3":
      throw new Error("AWS S3 provider not implemented yet");

    case "google_cloud":
      throw new Error("Google Cloud Storage provider not implemented yet");

    case "azure":
      throw new Error("Azure Storage provider not implemented yet");

    default:
      throw new Error(`Unsupported storage provider: ${provider}`);
  }
};

/**
 * Delete file from storage
 */
export const deleteFile = async (
  provider: StorageProvider,
  bucket: string,
  paths: string[],
): Promise<void> => {
  switch (provider) {
    case "supabase":
      return deleteFromSupabase(bucket, paths);

    case "s3":
      throw new Error("AWS S3 provider not implemented yet");

    case "google_cloud":
      throw new Error("Google Cloud Storage provider not implemented yet");

    case "azure":
      throw new Error("Azure Storage provider not implemented yet");

    default:
      throw new Error(`Unsupported storage provider: ${provider}`);
  }
};

/**
 * Get public URL for file
 */
export const getPublicUrl = (
  provider: StorageProvider,
  bucket: string,
  path: string,
): string => {
  switch (provider) {
    case "supabase":
      return getSupabasePublicUrl(bucket, path);

    case "s3":
      throw new Error("AWS S3 provider not implemented yet");

    case "google_cloud":
      throw new Error("Google Cloud Storage provider not implemented yet");

    case "azure":
      throw new Error("Azure Storage provider not implemented yet");

    default:
      throw new Error(`Unsupported storage provider: ${provider}`);
  }
};

// ============================================================================
// Supabase Storage Implementation
// ============================================================================

/**
 * Upload file to Supabase Storage
 */
const uploadToSupabase = async (
  bucket: string,
  path: string,
  file: File | Blob,
  options?: {
    contentType?: string;
    cacheControl?: string;
    upsert?: boolean;
  },
): Promise<string> => {
  const { data, error } = await supabase.storage
    .from(bucket)
    .upload(path, file, {
      contentType: options?.contentType,
      cacheControl: options?.cacheControl || "3600",
      upsert: options?.upsert || false,
    });

  if (error) {
    throw error;
  }
  if (!data) {
    throw new Error("Upload failed");
  }

  return data.path;
};

/**
 * Download file from Supabase Storage
 */
const downloadFromSupabase = async (
  bucket: string,
  path: string,
): Promise<Blob> => {
  const { data, error } = await supabase.storage.from(bucket).download(path);

  if (error) {
    throw error;
  }
  if (!data) {
    throw new Error("Download failed");
  }

  return data;
};

/**
 * Get signed URL from Supabase Storage
 */
const getSupabaseSignedUrl = async (
  bucket: string,
  path: string,
  expiresIn: number,
): Promise<string> => {
  const { data, error } = await supabase.storage
    .from(bucket)
    .createSignedUrl(path, expiresIn);

  if (error) {
    throw error;
  }
  if (!data) {
    throw new Error("Failed to generate signed URL");
  }

  return data.signedUrl;
};

/**
 * Delete files from Supabase Storage
 */
const deleteFromSupabase = async (
  bucket: string,
  paths: string[],
): Promise<void> => {
  const { error } = await supabase.storage.from(bucket).remove(paths);

  if (error) {
    throw error;
  }
};

/**
 * Get public URL from Supabase Storage
 */
const getSupabasePublicUrl = (bucket: string, path: string): string => {
  const { data } = supabase.storage.from(bucket).getPublicUrl(path);
  return data.publicUrl;
};

/**
 * List files in a bucket path
 */
export const listFiles = async (
  provider: StorageProvider,
  bucket: string,
  path: string = "",
): Promise<
  Array<{ name: string; id: string; metadata: Record<string, unknown> }>
> => {
  switch (provider) {
    case "supabase": {
      const { data, error } = await supabase.storage.from(bucket).list(path);
      if (error) {
        throw error;
      }
      return (data || []).map((file) => ({
        name: file.name,
        id: file.id || file.name,
        metadata: file.metadata || {},
      }));
    }
    default:
      throw new Error(`Unsupported storage provider: ${provider}`);
  }
};

/**
 * Copy file within storage
 */
export const copyFile = async (
  provider: StorageProvider,
  bucket: string,
  fromPath: string,
  toPath: string,
): Promise<string> => {
  switch (provider) {
    case "supabase": {
      const { data, error } = await supabase.storage
        .from(bucket)
        .copy(fromPath, toPath);
      if (error) {
        throw error;
      }
      return data.path;
    }
    default:
      throw new Error(`Unsupported storage provider: ${provider}`);
  }
};

/**
 * Move file within storage
 */
export const moveFile = async (
  provider: StorageProvider,
  bucket: string,
  fromPath: string,
  toPath: string,
): Promise<string> => {
  switch (provider) {
    case "supabase": {
      const { data, error } = await supabase.storage
        .from(bucket)
        .move(fromPath, toPath);
      if (error) {
        throw error;
      }
      return (data as unknown as { path: string })?.path || toPath;
    }
    default:
      throw new Error(`Unsupported storage provider: ${provider}`);
  }
};
